import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import portalApi, { getPortalToken, setPortalToken, portalMediaSrc } from "../../lib/portalApi";
import { ArrowLeft, Loader2, Check, Circle, Play, FileText, Download, MessageSquare, Send, CalendarClock, LogOut } from "lucide-react";

const STATUS_LABEL = { nouveau: "Nouveau", etude: "En étude", devis: "Devis", production: "En production", controle: "Contrôle qualité", termine: "Terminé", livree: "Livré", facturee: "Facturé" };
const euro = (n) => (n || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });

export default function PortalProject() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [msgInput, setMsgInput] = useState("");
  const [sending, setSending] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  const load = useCallback(() => {
    portalApi.get(`/portal/projects/${id}`).then((r) => setP(r.data))
      .catch((e) => { if (e?.response?.status === 401) { setPortalToken(null); navigate("/portail", { replace: true }); } else navigate("/portail/projets", { replace: true }); });
  }, [id, navigate]);

  useEffect(() => { if (!getPortalToken()) { navigate("/portail", { replace: true }); return; } load(); }, [load, navigate]);

  const send = async () => {
    if (!msgInput.trim()) return;
    setSending(true);
    try { const { data } = await portalApi.post(`/portal/projects/${id}/messages`, { body: msgInput }); setP((prev) => ({ ...prev, messages: [...(prev.messages || []), data] })); setMsgInput(""); }
    catch { /* noop */ } finally { setSending(false); }
  };

  const downloadDoc = async (docId) => {
    try {
      const res = await portalApi.get(`/portal/projects/${id}/documents/${docId}/pdf`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch { /* noop */ }
  };

  if (!p) return <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-gradient-to-r from-[#131b33] to-[#0b1224] text-white">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-3">
          <button onClick={() => navigate("/portail/projets")} data-testid="portal-back" className="p-2 rounded-lg hover:bg-white/10"><ArrowLeft className="w-5 h-5" /></button>
          <div className="min-w-0"><span className="text-[11px] font-mono text-slate-400">{p.reference}</span><h1 className="font-display font-bold text-lg truncate">{p.title}</h1></div>
          <button onClick={() => { setPortalToken(null); navigate("/portail", { replace: true }); }} data-testid="portal-logout" className="ml-auto inline-flex items-center gap-1.5 text-sm text-slate-300 hover:text-white shrink-0"><LogOut className="w-4 h-4" /> Quitter</button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-5">
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <span className="text-sm font-semibold text-cognac-600">{STATUS_LABEL[p.status] || p.status}</span>
            {p.due_date && <span className="text-xs text-slate-400 inline-flex items-center gap-1"><CalendarClock className="w-3 h-3" /> Échéance {p.due_date}</span>}
          </div>
          <div className="mt-2 flex items-center gap-3"><div className="flex-1 h-2.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-gradient-to-r from-cognac-400 to-cognac-500 rounded-full transition-all" style={{ width: `${p.progress}%` }} /></div><span className="text-sm font-bold text-navy tabular">{p.progress}%</span></div>
          {p.description && <p className="text-sm text-slate-500 mt-3">{p.description}</p>}
        </div>

        {/* Timeline */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="portal-timeline">
          <h2 className="font-display font-semibold text-navy mb-4">Avancement</h2>
          {(p.stages || []).length === 0 ? (
            <p className="text-sm text-slate-400">Votre artisan n'a pas encore publié d'étape. Revenez bientôt !</p>
          ) : (
            <div className="space-y-5">
              {p.stages.map((s, i) => (
                <div key={s.key || i} className="flex gap-3" data-testid={`portal-stage-${s.key}`}>
                  <div className="flex flex-col items-center">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${s.done ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-400"}`}>{s.done ? <Check className="w-4 h-4" /> : <Circle className="w-3 h-3" />}</div>
                    {i < p.stages.length - 1 && <div className="w-px flex-1 bg-slate-200 my-1" />}
                  </div>
                  <div className="flex-1 pb-1">
                    <div className="flex items-center gap-2 flex-wrap"><span className="font-semibold text-navy text-sm">{s.label}</span>{s.done && s.date && <span className="text-[11px] text-slate-400">{s.date}</span>}</div>
                    {s.description && <p className="text-sm text-slate-500 mt-0.5">{s.description}</p>}
                    {s.note && <p className="text-xs text-slate-400 mt-0.5">{s.note}</p>}
                    {(s.media || []).length > 0 && (
                      <div className="mt-2 grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid={`portal-media-${s.key}`}>
                        {s.media.map((m, mi) => (
                          <button key={mi} onClick={() => setLightbox(m)} className="relative aspect-square rounded-lg overflow-hidden border border-slate-200 group">
                            {m.type === "video"
                              ? <video src={portalMediaSrc(m.url)} className="w-full h-full object-cover" muted playsInline />
                              : <img src={portalMediaSrc(m.url)} alt="" className="w-full h-full object-cover" />}
                            {m.type === "video" && <span className="absolute inset-0 flex items-center justify-center bg-black/25"><Play className="w-5 h-5 text-white" /></span>}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Documents */}
        {(p.documents || []).length > 0 && (
          <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="portal-documents">
            <h2 className="font-display font-semibold text-navy mb-3">Mes documents</h2>
            <div className="space-y-2">
              {p.documents.map((d) => (
                <button key={d.id} onClick={() => downloadDoc(d.id)} data-testid={`portal-doc-${d.id}`} className="w-full flex items-center justify-between gap-2 py-2.5 px-3 rounded-xl border border-slate-100 hover:bg-slate-50">
                  <span className="flex items-center gap-2 min-w-0"><FileText className="w-4 h-4 text-sky-500 shrink-0" /><span className="text-left min-w-0"><span className="text-sm font-medium text-navy truncate block">{d.title || d.number}</span><span className="text-[11px] font-mono text-slate-400 capitalize">{d.type} · {d.number}</span></span></span>
                  <span className="flex items-center gap-3 shrink-0"><span className="text-sm font-semibold text-navy tabular">{euro(d.total_ttc)}</span><Download className="w-4 h-4 text-slate-400" /></span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Chat */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="portal-chat">
          <h2 className="font-display font-semibold text-navy mb-3 inline-flex items-center gap-2"><MessageSquare className="w-5 h-5 text-sky-500" /> Échanger avec l'atelier</h2>
          <div className="space-y-2 max-h-80 overflow-y-auto mb-3 pr-1">
            {(p.messages || []).map((m) => (
              <div key={m.id} className={`text-sm px-3 py-2 rounded-2xl max-w-[80%] ${m.sender_type === "client" ? "ml-auto bg-cognac-500 text-white" : "bg-slate-100 text-slate-700"}`}>
                <div className="text-[10px] opacity-70 mb-0.5">{m.sender_name} · {(m.at || "").slice(0, 16).replace("T", " ")}</div>{m.body}
              </div>
            ))}
            {(p.messages || []).length === 0 && <p className="text-xs text-slate-400 text-center py-4">Aucun message. Posez une question à votre artisan.</p>}
          </div>
          <div className="flex gap-2">
            <input value={msgInput} onChange={(e) => setMsgInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Votre message…" data-testid="portal-chat-input" className="flex-1 px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-cognac-400" />
            <button onClick={send} disabled={sending} data-testid="portal-chat-send" className="px-4 rounded-xl bg-cognac-500 text-white font-semibold hover:bg-cognac-600 disabled:opacity-60 flex items-center">{sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}</button>
          </div>
        </div>
      </main>

      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setLightbox(null)} data-testid="portal-lightbox">
          {lightbox.type === "video"
            ? <video src={portalMediaSrc(lightbox.url)} controls autoPlay className="max-w-full max-h-full rounded-xl" onClick={(e) => e.stopPropagation()} />
            : <img src={portalMediaSrc(lightbox.url)} alt="" className="max-w-full max-h-full rounded-xl" onClick={(e) => e.stopPropagation()} />}
        </div>
      )}
    </div>
  );
}
